<template>
  <div>
    <VToolbar>
      <VToolbarTitle
        color="secondary"
      >
        Import Contacts
      </VToolbarTitle>
      <VSpacer />
      <AsyncAutoComplete
        icon="groups"
        label="Select groups"
        multiple
        :default-array-select="selectedGroups"
        :get-gql-query="groupGqlQuery"
        :default-items="groups"
        :get-query-variables="getGroupVariables"
        :parse-data="parseGroupData"
        :set-query-status="setGroupQueryStatus"
        :get-query-status="getGroupQueryStatus"
        :update-context-variable="updateGroupContextVariable"
        :clear-all="clearGroups"
        @itemsChange="onGroupChange"
      />
      <AsyncAutoComplete
        icon="bookmark"
        label="Select categories"
        multiple
        :default-array-select="selectedCategories"
        :get-gql-query="categoryGqlQuery"
        :default-items="categories"
        :get-query-variables="getCategoryVariables"
        :parse-data="parseCategoryData"
        :set-query-status="setCategoryQueryStatus"
        :get-query-status="getCategoryQueryStatus"
        :update-context-variable="updateCategoryContextVariable"
        :clear-all="clearCategories"
        @itemsChange="onCategoryChange"
      />
      <VCheckbox
        v-model="isOverwritten"
        label="Over Write"
        color="red"
        hide-details
      />
      <VBtn
        icon
        @click="pickFile"
      >
        <VIcon
          color="primary"
        >
          create_new_folder
        </VIcon>
        <input
          ref="fileInput"
          type="file"
          style="display:none"
          @change="onLoadCSV"
        >
      </VBtn>
    </VToolbar>
    <VDataTable
      :headers="headers"
      :items="items"
      :loading="isDataLoading"
      class="elevation-1"
    >
      <template
        slot="headerCell"
        slot-scope="props"
      >
        <VLayout
          align-center
          wrap
        >
          <VFlex
            xs12
            sm12
          >
            <VSelect
              v-model="props.header.selectedField"
              :items="fields"
              :disabled="isIgnored(props.header.value)"
              label="Field"
              menu-props="auto"
            />
          </VFlex>
          <VFlex
            xs12
            sm12
          >
            <VCheckbox
              v-model="props.header.ignore"
              :label="`Ignore`"
              @change="onIgnoreClicked(props.header.value, props.header.ignore)"
            />
          </VFlex>
        </VLayout>
        <div
          :class="{ inactive: isIgnored(props.header.value)}"
          class="subheading"
        >
          {{ props.header.text }}
        </div>
      </template>
      <VProgressLinear
        slot="progress"
        color="blue"
        indeterminate
      />
      <template
        slot="items"
        slot-scope="props"
      >
        <tr class="grey-text text-lighten-5">
          <td
            v-for="(value, key) in props.item"
            :key="key"
            :class="{ inactive: isIgnored(key)}"
          >
            {{ value }}
          </td>
        </tr>
      </template>
      <VAlert
        slot="no-result"
        :value="true"
        color="error"
        icon="warning"
      >
        Your search for "{{ search }}" found no results.
      </VAlert>
    </VDataTable>
    <div class="text-xs-center pt-2">
      <VBtn
        color="primary"
        @click.native="submit"
      >
        Submit
      </VBtn>
    </div>
    <NetworkSnackBar />
    <DescriptionDialogCard
      :title="dialogTitle"
      :description="dialogDescription"
      :dialog.sync="dialog"
      :width="dialogWidth"
    />
    <ProgressDialogCard
      :description="progressBarText"
      :dialog.sync="progressBarStatus"
      :progress-value.sync="progressBarValue"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { categoryMapActions, groupMapActions } from '@/store/utils'
import DescriptionDialogCard from '../components/DescriptionDialogCard'
import ProgressDialogCard from '@/components/ProgressDialogCard'
import _ from 'lodash'
import libphonenumber from 'google-libphonenumber'
import NetworkSnackBar from '../components/NetworkSnackBar'
import AsyncAutoComplete from '../components/AsyncAutoComplete'
import { LIST_QUERY_CATEGORIES } from '@/backend/graphql/categoryGQL'
import { LIST_QUERY_GROUPS } from '@/backend/graphql/groupGQL'

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()

export default {
  name: 'ImportCSVContactPage',
  components: { AsyncAutoComplete, NetworkSnackBar, DescriptionDialogCard, ProgressDialogCard },
  filters: {
    capitalize: function (str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    }
  },
  data () {
    return {
      groupGqlQuery: LIST_QUERY_GROUPS,
      categoryGqlQuery: LIST_QUERY_CATEGORIES,
      headers: [],
      items: [],
      search: '',
      isDataLoading: false,
      selectedGroups: [],
      groupName: '',
      selectedCategories: [],
      categoryName: '',
      fields: ['Full Name', 'First Name', 'Last Name', 'Number', 'Organization', 'Title', 'Email',
        'Birthday', 'Notes'],
      field_keys: ['fullName', 'name', 'lastName', 'number', 'organization', 'title', 'email',
        'birthday', 'description'],
      ignoredColumn: [],
      dialog: false,
      dialogWidth: 400,
      dialogTitle: '',
      dialogDescription: [],
      progressDialog: false,
      limit: 30,
      offset: 0,
      orderBy: 'name',
      isOverwritten: false
    }
  },
  computed: {
    ...mapGetters({
      getLoadingStatus: 'contact/getLoadingStatus',
      getLoadingText: 'contact/getLoadingText',
      getProgressValue: 'contact/getProgressValue'
    }),
    progressBarStatus: {
      get: function () {
        return this.getLoadingStatus
      },
      set: function (val) {
        this.setLoadingStatus({ val })
      }
    },
    progressBarText: {
      get: function () {
        return this.getLoadingText
      },
      set: function (message) {
        this.setLoadingText({ message })
      }
    },
    progressBarValue: {
      get: function () {
        return this.getProgressValue
      },
      set: function (val) {
        this.setProgressValue({ val })
      }
    },
    groups: {
      get: function () {
        return this.$store.getters['group/getAllGroups'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        this.selectedGroups = val.map(item => { return _.startCase(item.trim()) })
      }
    },
    categories: {
      get: function () {
        return this.$store.getters['category/getAllCategories'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        this.selectedCategories = val.map(item => { return _.startCase(item.trim()) })
      }
    }
  },
  watch: {
    getProgressValue: function () {
      this.progressBarValue = this.getProgressValue
    }
  },
  created () {
    this.setEditMenuItems({ val: [] })
    this.setMessageDrawerVisibility({ val: false })
  },
  methods: {
    ...mapActions(Object.assign({
      setLoadingStatus: 'contact/setLoadingStatus',
      setLoadingText: 'contact/setStatusMessage',
      setProgressValue: 'contact/setProgressValue',
      uploadContacts: 'contact/uploadContacts',
      setEditMenuItems: 'toolBar/setEditMenuItems',
      setMessageDrawerVisibility: 'messageDrawer/setVisibility'
    }, groupMapActions, categoryMapActions)),
    pickFile () {
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },
    isIgnored (key) {
      return this.ignoredColumn.includes(key)
    },
    onIgnoreClicked (key, value) {
      if (!value) {
        this.ignoredColumn.splice(this.ignoredColumn.indexOf(key), 1)
      } else {
        this.ignoredColumn.push(key)
      }
    },
    removeCategory (item) {
      this.selectedCategories.splice(this.selectedCategories.indexOf(item), 1)
      this.selectedCategories = [...this.selectedCategories]
    },
    onLoadCSV (e) {
      if (window.FileReader) {
        const vm = this
        const reader = new FileReader()
        reader.readAsText(e.target.files[0])
        reader.onload = function (event) {
          vm.isDataLoading = true
          vm.headers = []
          vm.items = []
          vm.parseCSV(event.target.result, vm)
        }
        reader.onerror = (event) => {
          if (event.target.error.name === 'NotReadableError') {
            this.$store.dispatch('alert/error', 'FileReader are not supported in this browser.')
          }
        }
      } else {
        this.$store.dispatch('alert/error', 'FileReader are not supported in this browser.')
      }
    },
    async parseCSV (csv, vm) {
      const result = await new Promise((resolve) => {
        let result = {}
        const lines = csv.split('\n')
        const headers = lines[0].split(',')
        result.headers = headers.map(item => ({
          text: item,
          value: item,
          sortable: false,
          selectedField: '',
          ignore: false
        }))
        result.items = []
        lines.slice(1).map((line) => {
          const currentLine = line.split(',')
          let obj = {}
          result.items.push(obj)
          result.headers.map((headerItem, headerIndex) => {
            obj[headerItem.value] = currentLine[headerIndex]
          })
        })
        resolve(result)
      })
      vm.headers = result.headers
      vm.items = result.items
      vm.isDataLoading = false
    },
    submit () {
      const dataMapped = this.allDataMapped()
      const uploadContactJSON = this.getUploadContactJSON()
      const dataValid = this.isDataValid(uploadContactJSON)
      if (!dataMapped || !dataValid) {
        this.dialogDescription = []
        if (this.headers.length === 0) {
          this.dialogDescription.push('No data imported.')
          this.dialogDescription.push('Please make sure you have data imported.')
        } else if (!dataValid) {
          this.dialogDescription.push('Fail to import contacts.')
          this.dialogDescription.push('1. Please make sure you select one column as the number field.')
          this.dialogDescription.push('2. Each column should be either ignored or connected to a field.')
          this.dialogDescription.push('3. Each column should be connected to a different field.')
        } else {
          this.dialogDescription.push('Fail to import contacts.')
          this.dialogDescription.push('Please make sure each column is either ignored or connected to a field.')
        }
        this.dialogTitle = 'Error'
        this.dialog = true
      } else {
        this.progressBarText = 'Uploading contacts. Please wait...'
        this.progressBarStatus = true
        this.progressBarValue = 0.0
        this.uploadContacts({ contactArray: uploadContactJSON })
      }
    },
    allDataMapped () {
      return this.headers.length === 0 ? false
        : this.headers.every((item) => (item.ignore ? true : !!item.selectedField))
    },
    getUploadContactJSON () {
      /**
       *  {
       *    isOverwritten : false,
       *    contacts: [
       *      { number: '',
       *        name: '',
       *        lastName: '',
       *        email: '',
       *        description: '',
       *        birthyear: '',
       *        bdayTags: [
       *          {
       *            strValue: '11/02',
       *            intValue: '1102',
       *            name: 'birthday'
       *          }
       *        ],
       *        profileWorkHistory: [
       *          {
       *            organization: {
       *              name: ''
       *            },
       *            title: {
       *              name: ''
       *            }
       *          }
       *        ]
       *      }
       *    ],
       *    groups: [
       *      {
       *        name: ''
       *      }
       *    ],
       *    categories: [
       *      {
       *        name: ''
       *      }
       *    ],
       *    interests: [
       *      {
       *        name: ''
       *      }
       *    ]
       *  }
       */
      let result = {}
      const contacts = this.getUploadContacts()
      const groups = this.getUploadGroups()
      const categories = this.getUploadCategories()
      const interests = this.getUploadInterests()
      if (_.isEmpty(contacts)) return result
      result['contacts'] = contacts
      result['groups'] = groups
      result['categories'] = categories
      result['interests'] = interests
      result['isOverwritten'] = this.isOverwritten
      return result
    },
    getUploadContacts () {
      // return an empty array if it has duplicated mapped headers
      let contacts = []
      let headerSelectedFields = {}
      let keySet = new Set()
      let numberHeaderKey = ''
      const allUnique = this.headers.every((item) => {
        if (item.ignore) return true
        const key = this.field_keys[this.fields.indexOf(item.selectedField)]
        if (keySet.has(key)) return false
        keySet.add(key)
        headerSelectedFields[item.value] = key
        if (key === 'number') numberHeaderKey = item.value
        return true
      })
      if (!allUnique) return contacts
      let phoneNumberSet = new Set()
      this.items.forEach((item) => {
        const number = _.get(item, numberHeaderKey, '')
        if (!number) return
        let numberInstance = null
        try {
          numberInstance = phoneUtil.parseAndKeepRawInput(number, 'US')
        } catch (e) {
          console.warn(number, e)
          return
        }
        if (!phoneUtil.isValidNumber(numberInstance) ||
          phoneNumberSet.has(numberInstance.getNationalNumber())) return
        phoneNumberSet.add(numberInstance.getNationalNumber())
        let obj = {}
        Object.keys(item).forEach((header) => {
          if (headerSelectedFields.hasOwnProperty(header)) {
            this.processUploadContactData(obj, headerSelectedFields[header], item[header])
          }
        })
        if (!_.isEmpty(obj)) contacts.push(obj)
      })
      return contacts
    },
    processUploadContactData (obj, objKey, value) {
      let cleanedValue = value.replace(/\r?\n|\r/, '')
      if (!cleanedValue) return
      switch (objKey) {
        case 'organization':
          if (!obj.hasOwnProperty('profileWorkHistory')) {
            obj['profileWorkHistory'] = [{}]
          }
          let profileWorkHistory = Object.assign(obj['profileWorkHistory'][0], { organization: { name: cleanedValue } })
          obj['profileWorkHistory'].splice(0, 1, profileWorkHistory)
          break
        case 'title': {
          if (!obj.hasOwnProperty('profileWorkHistory')) {
            obj['profileWorkHistory'] = [{}]
          }
          let profileWorkHistory = Object.assign(obj['profileWorkHistory'][0], { title: { name: cleanedValue } })
          obj['profileWorkHistory'].splice(0, 1, profileWorkHistory)
          break
        }
        case 'birthday': {
          try {
            let birthday = new Date(cleanedValue)
            obj['birthyear'] = birthday.getFullYear()
            if (!obj.hasOwnProperty('bdayTags')) {
              obj['bdayTags'] = [{}]
            }
            const birthMonth = String(birthday.getMonth() + 1)
            let birthDate = String(birthday.getDate())
            if (birthDate.length < 2) birthDate = '0' + birthDate
            let bdayTag = Object.assign(obj['bdayTags'][0], { strValue: birthMonth + birthDate,
              intValue: parseInt(birthMonth + birthDate),
              name: 'birthday' })
            obj['bdayTags'].splice(0, 1, bdayTag)
          } catch (e) {
            console.warn(cleanedValue, e)
          }
          break
        }
        default: {
          obj[objKey] = cleanedValue
          break
        }
      }
    },
    getUploadGroups () {
      return this.selectedGroups.map((item) => ({ name: item }))
    },
    getUploadCategories () {
      return this.selectedCategories.map(item => ({ name: item }))
    },
    getUploadInterests () {
      // TODO: add interests to upload
      return []
    },
    isDataValid (data) {
      return data && Object.keys(data).length > 0 && data.hasOwnProperty('contacts') &&
        data['contacts'].length > 0 && data['contacts'][0].hasOwnProperty('number')
    },
    getGroupVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        orderBy: this.orderBy,
        name: this.groupName
      }
    },
    getGroupItems (data) { return data.listGroups.results },
    getTotalGroupCount (data) { return data.listGroups.totalCount },
    parseGroupData (data) {
      if (this.groups.length === 0) {
        this.initGroups({
          newGroups: this.getGroupItems(data),
          totalCount: this.getTotalGroupCount(data),
          isFullArraySize: false
        })
      } else {
        this.clearGroups()
        this.initGroups({
          newGroups: this.getGroupItems(data),
          totalCount: this.getTotalGroupCount(data),
          isFullArraySize: false
        })
      }
    },
    onGroupChange (value) {
      this.groups = value
    },
    updateGroupContextVariable (key, value) {
      if (key === 'name') key = 'groupName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    getGroupQueryStatus () {
      return this.$store.getters['group/getLoadingStatus']
    },
    setGroupQueryStatus (val) {
      this.$store.dispatch('group/setLoadingStatus', { val })
    },
    getCategoryVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        orderBy: this.orderBy,
        name: this.categoryName
      }
    },
    getCategoryItems (data) { return data.listCategories.results },
    getTotalCategoryCount (data) { return data.listCategories.totalCount },
    parseCategoryData (data) {
      if (this.categories.length === 0) {
        this.initCategories({
          newCategories: this.getCategoryItems(data),
          totalCount: this.getTotalCategoryCount(data),
          isFullArraySize: false
        })
      } else {
        this.clearCategories()
        this.initCategories({
          newCategories: this.getCategoryItems(data),
          totalCount: this.getTotalCategoryCount(data),
          isFullArraySize: false
        })
      }
    },
    onCategoryChange (value) {
      this.categories = value
    },
    updateCategoryContextVariable (key, value) {
      if (key === 'name') key = 'categoryName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    getCategoryQueryStatus () {
      return this.$store.getters['category/getLoadingStatus']
    },
    setCategoryQueryStatus (val) {
      this.$store.dispatch('category/setLoadingStatus', { val })
    }
  }
}
</script>

<style scoped>
  .inactive {
    color: #E0E0E0
  }
</style>
